import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Lock } from "lucide-react";
import { toast } from "sonner";

const empty = {
  mode: "deals", name: "", tagline: "", description: "", business_type: "", logo_path: "", cover_path: "",
  phone: "", email: "", whatsapp: "", website: "", address: "", map_query: "",
  facebook: "", instagram: "", twitter: "", linkedin: "", published: true,
  custom_domain: "", custom_domain_verified: false, custom_domain_token: "",
  theme: "maroon",
};

const THEMES = [
  { id: "maroon", label: "Maroon", swatch: ["#e63946", "#800000", "#0a0505"] },
  { id: "emerald", label: "Emerald", swatch: ["#2fbf83", "#8a6a1e", "#08110c"] },
  { id: "azure", label: "Azure", swatch: ["#2f9de6", "#1e3a6e", "#080d14"] },
];

export default function BusinessProfile() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domain, setDomain] = useState("");
  const [domainSaving, setDomainSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [myModules, setMyModules] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [biz, plans] = await Promise.all([api.get("/business/me"), api.get("/billing/plans")]);
        if (biz.data) { setForm({ ...empty, ...biz.data }); setDomain(biz.data.custom_domain || ""); }
        const myPlan = plans.data.plans.find((p) => p.id === user?.subscription?.plan);
        setMyModules(myPlan?.modules || []);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trial gets every module (matches the backend's get_user_modules()) — no locks shown during trial.
  const hasModule = (m) => user?.subscription?.status === "trial" || myModules.includes(m);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Business name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/business", form);
      setForm({ ...empty, ...data });
      await checkAuth();
      toast.success("Business profile saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveDomain = async () => {
    if (!domain.trim()) return toast.error("Enter a domain");
    setDomainSaving(true);
    try {
      const { data } = await api.post("/business/custom-domain", { domain: domain.trim() });
      setForm((f) => ({ ...f, ...data.business }));
      toast.success("Domain saved — add the DNS record below, then verify");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save domain");
    } finally {
      setDomainSaving(false);
    }
  };

  const verifyDomain = async () => {
    setVerifying(true);
    try {
      const { data } = await api.get("/business/custom-domain/status");
      setDnsStatus(data);
      if (data.verified) {
        setForm((f) => ({ ...f, custom_domain_verified: true }));
        toast.success(data.points_here ? "Domain verified and live!" : "Ownership verified — now point your domain at DealWisp");
      } else toast.error("TXT record not found yet — DNS can take a while to propagate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Not verified yet — DNS can take a while to propagate");
    } finally {
      setVerifying(false);
    }
  };

  const [dnsStatus, setDnsStatus] = useState(null);

  const removeDomain = async () => {
    await api.delete("/business/custom-domain");
    setForm((f) => ({ ...f, custom_domain: "", custom_domain_verified: false, custom_domain_token: "" }));
    setDomain("");
    toast.success("Custom domain removed");
  };

  if (loading) return (<div className="min-h-screen"><Navbar /><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></div>);

  const field = (label, key, placeholder = "", type = "text") => (
    <div>
      <Label className="overline text-muted-foreground">{label}</Label>
      <Input type={type} value={form[key]} onChange={set(key)} placeholder={placeholder} data-testid={`biz-${key}`} className="mt-2 bg-background border-white/10" />
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between">
          <div>
            <span className="overline text-primary">Business</span>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Business profile</h1>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-secondary" data-testid="save-business-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
          </Button>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <ImageUpload label="Logo" value={form.logo_path} onChange={set("logo_path")} testid="upload-logo" aspect="aspect-square" />
          <ImageUpload label="Cover image" value={form.cover_path} onChange={set("cover_path")} testid="upload-cover" aspect="aspect-square" />
        </div>

        <div className="mt-4 bg-card border border-white/10 rounded-xl p-6 space-y-4">
          <h3 className="font-display font-semibold">Basics</h3>
          {field("Business name *", "name", "The Corner Cafe")}
          {field("Business type", "business_type", "Cafe / Boutique / Salon")}
          <div>
            <Label className="overline text-muted-foreground">How customers buy</Label>
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="biz-mode">
              {[["deals", "Offers & deals", "Customers browse, then WhatsApp you"], ["food", "Food pre-orders", "Cart + order with a token number"]].map(([v, t, d]) => (
                <button type="button" key={v} onClick={() => setForm((f) => ({ ...f, mode: v }))} data-testid={`mode-${v}`}
                  className={`text-left rounded-lg border px-3 py-2 ${form.mode === v ? "border-primary bg-primary/10" : "border-white/10"}`}>
                  <span className="text-sm font-medium block">{t}</span><span className="text-xs text-muted-foreground">{d}</span>
                </button>
              ))}
            </div>
          </div>
          {field("Tagline", "tagline", "Freshly brewed, always.")}
          <div>
            <Label className="overline text-muted-foreground">Description</Label>
            <Textarea value={form.description} onChange={set("description")} placeholder="Tell customers about your business" data-testid="biz-description" className="mt-2 bg-background border-white/10 min-h-24" />
          </div>
          <div className="flex items-center justify-between pt-2">
            <div>
              <p className="text-sm font-medium">Published</p>
              <p className="text-xs text-muted-foreground">Make your public page visible to everyone</p>
            </div>
            <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="biz-published" />
          </div>
          <div className="pt-2">
            <p className="text-sm font-medium">Public page theme</p>
            <p className="text-xs text-muted-foreground mb-3">Changes the look of your public page only — your dashboard stays the same.</p>
            <div className="flex gap-3">
              {THEMES.map((t) => {
                const locked = t.id !== "maroon" && !hasModule("themes");
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => locked ? toast.error("Public-page themes aren't included in your plan — upgrade to unlock.") : setForm((f) => ({ ...f, theme: t.id }))}
                    className={`flex-1 rounded-xl border p-3 text-left transition-colors ${form.theme === t.id ? "border-primary" : "border-white/10 hover:border-white/25"} ${locked ? "opacity-50" : ""}`}
                    data-testid={`theme-${t.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex gap-1">
                        {t.swatch.map((c, i) => <span key={i} className="w-5 h-5 rounded-full border border-white/10" style={{ background: c }} />)}
                      </div>
                      {locked && <Lock className="w-3 h-3 text-muted-foreground" />}
                    </div>
                    <span className="text-sm">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-4 bg-card border border-white/10 rounded-xl p-6 space-y-4">
          <h3 className="font-display font-semibold">Contact</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {field("Phone", "phone", "+1 555 000 111")}
            {field("Email", "email", "hello@shop.com", "email")}
            {field("WhatsApp", "whatsapp", "+1 555 000 111")}
            {field("Website", "website", "https://shop.com")}
          </div>
          {field("Address", "address", "12 Market Street, Springfield")}
          {field("Map location (search text)", "map_query", "12 Market Street, Springfield")}
        </div>

        <div className="mt-4 bg-card border border-white/10 rounded-xl p-6 space-y-4">
          <h3 className="font-display font-semibold">Social links</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {field("Facebook", "facebook", "https://facebook.com/...")}
            {field("Instagram", "instagram", "https://instagram.com/...")}
            {field("Twitter / X", "twitter", "https://x.com/...")}
            {field("LinkedIn", "linkedin", "https://linkedin.com/...")}
          </div>
        </div>

        <div className="mt-4 bg-card border border-white/10 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold">Custom domain</h3>
            {form.custom_domain && (
              <span className={`text-xs px-2 py-1 rounded-full ${form.custom_domain_verified ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`} data-testid="domain-status">
                {form.custom_domain_verified ? "Verified" : "Pending verification"}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Point your own domain at your public page. Your host also needs to be configured to route that domain here — this only proves you own it.</p>
          {!hasModule("custom_domain") ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-background border border-white/10 rounded-lg p-3" data-testid="domain-locked">
              <Lock className="w-4 h-4 shrink-0" />
              {form.custom_domain
                ? "Your plan no longer includes custom domains — upgrade to edit or re-verify this one."
                : "Custom domains aren't included in your plan — upgrade to unlock."}
            </div>
          ) : (
            <div className="flex gap-2">
              <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="shop.com" data-testid="biz-custom-domain" className="bg-background border-white/10" />
              <Button onClick={saveDomain} disabled={domainSaving} variant="outline" className="rounded-full border-white/15 shrink-0" data-testid="save-domain-btn">
                {domainSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          )}
          {form.custom_domain && (
            <div className="bg-background border border-white/10 rounded-lg p-3 text-xs space-y-3" data-testid="domain-instructions">
              <div>
                <p className="font-semibold">Step 1 — prove ownership (TXT record)</p>
                <p className="text-muted-foreground">Type: TXT · Name: <span className="font-mono text-white" data-testid="domain-txt-name">_dealhub-verify.{form.custom_domain}</span></p>
                <p className="text-muted-foreground">Value: <span className="font-mono text-primary break-all" data-testid="domain-txt-value">{form.custom_domain_token}</span></p>
              </div>
              <div>
                <p className="font-semibold">Step 2 — point the domain here (A or CNAME record)</p>
                <p className="text-muted-foreground">Add a CNAME for <span className="font-mono text-white">{form.custom_domain}</span> pointing to <span className="font-mono text-primary">dealwisp.online</span> (or an A record to the same IP as dealwisp.online). Then tell support/admin to add the domain so HTTPS is issued.</p>
              </div>
              {dnsStatus && (
                <ul className="space-y-1">
                  <li>{dnsStatus.txt_ok || form.custom_domain_verified ? "✅" : "⏳"} Ownership TXT record</li>
                  <li>{dnsStatus.points_here ? "✅" : "⏳"} Domain points to DealWisp {dnsStatus.resolved_ips?.length ? `(now: ${dnsStatus.resolved_ips.join(", ")})` : "(no A record found yet)"}</li>
                </ul>
              )}
              <Button size="sm" onClick={verifyDomain} disabled={verifying} className="rounded-full bg-primary hover:bg-secondary" data-testid="verify-domain-btn">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check DNS now"}
              </Button>
            </div>
          )}
          {form.custom_domain && (
            <Button size="sm" variant="ghost" onClick={removeDomain} className="text-primary" data-testid="remove-domain-btn">Remove domain</Button>
          )}
        </div>
      </main>
    </div>
  );
}
